<template>
  <div class="mouse px">
    <step-loader
      :visible="stepLoaderVisible"
      :steps="stepLoaderSteps"
      :currentStep="stepLoaderStep"
    />
    <c-mouse-electricity ref="mouseElectricity" />
    <div class="fixed-panel px">
      <div class="mouse-panel info">
        <div
          class="keys"
          :style="{
            transform: `rotateX(${rotateX}) rotateY(${rotateY})`,
            transformOrigin: 'center',
          }"
        >
          <div
            class="key"
            v-for="(key, i) in keys"
            :key="key.key"
            :class="{
              ['key' + (i + 1)]: true,
              active: key.key === activeKey,
              disabled: keyDisabled(key.key),
            }"
            @click="onMouseClick(key.key)"
          >
            {{ keyDesc(key.key) }}
          </div>
        </div>
      </div>
      <div class="radio-panel">
        <div class="tab-group function-tabs">
          <a-radio-group class="virtual-radio" v-model="type" size="large">
            <a-radio-button key="keymap" value="keymap">
              {{ $t("keymap") }}
            </a-radio-button>
            <a-radio-button key="macro" value="macro">
              {{ $t("macro") }}
            </a-radio-button>
            <a-radio-button key="dpi" value="dpi">
              DPI
            </a-radio-button>
            <a-radio-button key="param" value="param">
              {{ $t("mouse.param") }}
            </a-radio-button>
            <a-radio-button key="light" value="light">
              {{ $t("mouse.light") }}
            </a-radio-button>
            <a-radio-button key="reset" value="reset" @click="onReset">
              {{ $t("reset") }}
            </a-radio-button>
          </a-radio-group>
        </div>
        <div class="tab-group profile-action-tabs">
          <a-dropdown
            class="profile-dropdown"
            :trigger="['click']"
            overlayClassName="am30-profile-dropdown"
            transitionName="slide-up"
          >
            <div
              class="profile-trigger tab-trigger"
              :class="{ disabled: isProfileSwitching }"
              @click.prevent
            >
              <span class="profile-trigger-label">{{ currentProfileLabel }}</span>
              <span class="profile-trigger-arrow">▼</span>
            </div>
            <div slot="overlay" class="am30-profile-menu">
              <div
                class="am30-profile-switch-row"
                :class="{ disabled: isProfileSwitching }"
                @click.stop
                @mousedown.stop
              >
                <span class="am30-profile-switch-label">
                  {{ $t("mouse.comboProfileSwitch") }}
                </span>
                <md-switch
                  class="md-primary am30-profile-switch-control"
                  v-model="am30ProfileKeySwitchEnabled"
                  :disabled="isProfileSwitching"
                ></md-switch>
              </div>
              <div
                v-for="profileOption in profileMenuOptions"
                :key="profileOption.value"
                class="am30-profile-menu-item"
                :class="{
                  active: profileOption.value === selectedMouseProfile,
                  disabled: isProfileSwitching,
                }"
                @click="onSwitchProfile(profileOption.value)"
              >
                <span class="am30-profile-menu-dot" :class="profileOption.dotClass"></span>
                <span class="am30-profile-menu-text">{{ profileOption.menuLabel }}</span>
              </div>
            </div>
          </a-dropdown>
          <a-radio-group class="virtual-radio action-radio" v-model="type" size="large">
            <a-radio-button key="upload" value="upload" @click="onUpload">
              <a-tooltip>
                <template slot="title">
                  <span style="color: #fff;">{{ $t("downloadTip") }}</span>
                </template>
                {{ $t("import") }}
              </a-tooltip>
            </a-radio-button>
            <a-radio-button
              key="download"
              value="download"
              @click="selectVisible = true"
            >
              <a-tooltip>
                <template slot="title">
                  <span style="color: #fff;">{{ $t("saveTip") }}</span>
                </template>
                {{ $t("export") }}
              </a-tooltip>
              <a-modal
                v-model="selectVisible"
                width="80vw"
                :footer="null"
                :centered="true"
                :closable="false"
                :maskClosable="false"
                :maskStyle="{ background: '#1c1c1c' }"
              >
                <div class="c-select">
                  <div class="title">
                    {{ $t("selectVersion.select") }}
                  </div>
                  <md-checkbox v-model="selectKeymap" class="checkbox">
                    {{ $t("keymap") }}
                  </md-checkbox>
                  <md-checkbox v-model="selectMacro" class="checkbox">
                    {{ $t("macro") }}
                  </md-checkbox>
                  <md-checkbox v-model="selectDpi" class="checkbox">
                    DPI
                  </md-checkbox>
                  <md-checkbox v-model="selectParam" class="checkbox">
                    {{ $t("mouse.param") }} & {{ $t("mouse.light") }}
                  </md-checkbox>
                  <div class="buttons">
                    <md-button class="button" @click="selectVisible = false">
                      {{ $t("cancel") }}
                    </md-button>
                    <md-button class="button" @click="onDownload">
                      {{ $t("save") }}
                    </md-button>
                  </div>
                </div>
              </a-modal>
            </a-radio-button>
          </a-radio-group>
        </div>
      </div>
    </div>
    <div class="settings-panel">
      <div class="panels" v-if="type === 'keymap'">
        <div class="panel" :class="{ keybord: mouseOrKeybord === 'keybord' }">
          <div class="panel-content">
            <div class="title">
              <div
                class="half"
                :class="{ active: mouseOrKeybord === 'mouse' }"
                @click="mouseOrKeybord = 'mouse'"
              >
                {{ $t("mouse.mouseKeys") }}
              </div>
              <div
                class="half"
                :class="{ active: mouseOrKeybord === 'keybord' }"
                @click="mouseOrKeybord = 'keybord'"
              >
                {{ $t("mouse.keybordKey") }}
              </div>
            </div>
            <a-radio-group
              :value="selectedKey"
              @change="onSelectedKey"
              class="radio-group"
              v-if="mouseOrKeybord === 'mouse'"
            >
              <a-radio
                class="radio"
                v-for="key in mouseKeys"
                :key="key.key.toString()"
                :value="key.key.toString()"
              >
                {{ $t("mouse.macroDesc." + key.desc) }}
              </a-radio>
            </a-radio-group>

            <c-mouse-virtual-keyboard
              v-if="mouseOrKeybord === 'keybord'"
              :selectedKey="keyMatrix[activeKey][3]"
              @change="onSelectedKeyboard($event)"
            />
          </div>
        </div>
        <div class="panel">
          <div class="panel-content">
            <div class="title">{{ $t("mouse.basicKeys") }}</div>
            <a-radio-group
              :value="selectedKey"
              @change="onSelectedKey"
              class="radio-group"
            >
              <a-radio
                class="radio"
                v-for="key in basicKeys"
                :key="key.key.toString()"
                :value="key.key.toString()"
              >
                {{ $t("mouse.macroDesc." + key.desc) }}
              </a-radio>
            </a-radio-group>
          </div>
        </div>
        <div class="panel" :class="{ hotkey: hotkeyOrMacro === 'hotkey' }">
          <div class="panel-content">
            <div class="title">
              <div
                class="half"
                :class="{ active: hotkeyOrMacro === 'hotkey' }"
                @click="hotkeyOrMacro = 'hotkey'"
              >
                {{ $t("mouse.hotkey") }}
              </div>
              <div
                class="half"
                :class="{ active: hotkeyOrMacro === 'macro' }"
                @click="hotkeyOrMacro = 'macro'"
              >
                {{ $t("mouse.macro") }}
              </div>
            </div>
            <template v-if="hotkeyOrMacro === 'hotkey'">
              <div class="checkbox-group">
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][1]"
                  @change="onSelectedHotkey(1, $event)"
                  value="0xe0"
                >
                  Ctrl
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][1]"
                  @change="onSelectedHotkey(1, $event)"
                  value="0xe1"
                >
                  Shift
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][1]"
                  @change="onSelectedHotkey(1, $event)"
                  value="0xe2"
                >
                  Alt
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][1]"
                  @change="onSelectedHotkey(1, $event)"
                  value="0xe3"
                >
                  Code
                </md-checkbox>
              </div>
              <span style="font-size: 0.3rem;">+</span>
              <div class="checkbox-group">
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe0"
                >
                  Ctrl
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe1"
                >
                  Shift
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe2"
                >
                  Alt
                </md-checkbox>
                <md-checkbox
                  class="checkbox"
                  v-model="keyMatrix[activeKey][2]"
                  @change="onSelectedHotkey(2, $event)"
                  value="0xe3"
                >
                  Code
                </md-checkbox>
              </div>
              <span style="font-size: 0.3rem;">+</span>
              <c-mouse-virtual-keyboard
                :selectedKey="keyMatrix[activeKey][3]"
                @change="onSelectedHotkey(3, $event)"
              />
              <div class="sub-title sub-title-with-tip">
                <span>{{ $t("mouse.clickMacro") }}</span>
                <a-popover placement="top">
                  <template slot="content">
                    <div class="name-question" v-html="$t('mouse.clickMacroTooltip')"></div>
                  </template>
                  <a-icon class="name-tip" type="question-circle" />
                </a-popover>
              </div>
              <c-mouse-virtual-keyboard
                :selectedKey="
                  keyMatrix[activeKey][0] === '0x0b'
                    ? keyMatrix[activeKey][1]
                    : null
                "
                @change="onSelectedFirepower(1, $event)"
                showMouse
              />
              {{ $t("mouse.interval") }}
              <md-field class="repeat">
                <md-input
                  class="repeat"
                  :value="
                    keyMatrix[activeKey][0] === '0x0b'
                      ? Number(keyMatrix[activeKey][2])
                      : null
                  "
                  @change="onSelectedFirepower(2, $event.target.value)"
                  type="number"
                  min="1"
                  max="255"
                  step="1"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 255 ? 255 : this.value;
                  this.value = this.value < 1 ? 1 : this.value;"
                ></md-input>
                <span class="md-suffix">ms</span>
              </md-field>
              <span style="color: transparent;">---</span>
              <a-popover placement="top">
                <template slot="content">
                  <div class="name-question">
                    {{ $t("mouse.repeatTip") }}
                  </div>
                </template>
                {{ $t("mouse.repeat") }}
              </a-popover>
              <md-field class="repeat">
                <md-input
                  class="repeat"
                  :value="
                    keyMatrix[activeKey][0] === '0x0b'
                      ? Number(keyMatrix[activeKey][3])
                      : null
                  "
                  @change="onSelectedFirepower(3, $event.target.value)"
                  type="number"
                  min="0"
                  max="255"
                  step="1"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 255 ? 255 : this.value;
                  this.value = this.value < 0 ? 0 : this.value;"
                ></md-input>
              </md-field>
            </template>
            <template v-else>
              <a-radio-group
                :value="selectedMacroIndex"
                @change="onSelectedMacro"
                class="radio-group"
              >
                <a-radio
                  class="radio"
                  v-for="macro in macroData"
                  :key="macro.index"
                  :value="macro.index"
                >
                  {{ macro.macro_name }}
                </a-radio>
              </a-radio-group>
              <a-radio-group
                :value="playModel"
                @change="onSelectedPlayModel"
                class="play-model"
                v-show="selectedMacroIndex >= 0"
              >
                <a-radio class="radio" value="0x00">
                  <a-popover placement="top">
                    <template slot="content">
                      <div class="name-question">
                        {{ $t("mouse.repeatTip") }}
                      </div>
                    </template>
                    {{ $t("mouse.repeat") }}
                  </a-popover>
                  <md-field class="repeat">
                    <md-input
                      class="repeat"
                      :value="repeat"
                      @change="onRepeatChange"
                      :disabled="playModel !== '0x00'"
                      min="1"
                      max="65535"
                      step="1"
                      oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 65535 ? 65535 : this.value;
                  this.value = this.value < 1 ? 1 : this.value;"
                    ></md-input>
                  </md-field>
                </a-radio>
                <a-radio class="radio" value="0x01">
                  <a-popover placement="top">
                    <template slot="content">
                      <div class="name-question">
                        {{ $t("mouse.autoTip") }}
                      </div>
                    </template>
                    {{ $t("mouse.auto") }}
                  </a-popover>
                </a-radio>
                <a-radio class="radio" value="0x02">
                  <a-popover placement="top">
                    <template slot="content">
                      <div class="name-question">
                        {{ $t("mouse.pressTip") }}
                      </div>
                    </template>
                    {{ $t("mouse.press") }}
                  </a-popover>
                </a-radio>
              </a-radio-group>
            </template>
          </div>
        </div>

        <div class="buttons">
          <div class="button" @click="onDisable">{{ $t("mouse.disable") }}</div>
          <div class="button" @click="onRestore">{{ $t("mouse.restore") }}</div>
        </div>
      </div>
      <c-mouse-macro v-if="type === 'macro'" />
      <c-mouse-dpi v-if="type === 'dpi'" />
      <c-mouse-param v-if="type === 'param'" />
      <c-mouse-light v-if="type === 'light'" />
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { keys, mouseKeys, basicKeys, allKeys } from "./mouse";
import VirtualKeyboard from "./components/VirtualKeyboard";
import StepLoader from "@/views/MouseAM35/components/StepLoader";
import stepLoaderMixin from "@/mixins/stepLoaderMixin";
import { deviceCallbackManager } from "@/utils/DeviceCallbackManager";

const SPECIAL_KEY_DESC_MAP = {
  ["0x00,0x00,0xe3,0x2d"]: "zoomOut",
  ["0x00,0x00,0xe3,0x2e"]: "zoomIn",
};

export default {
  mixins: [stepLoaderMixin],
  components: {
    StepLoader,
  },
  data() {
    return {
      keys,
      mouseKeys,
      basicKeys,
      allKeys,
      rotateX: "0deg",
      rotateY: "0deg",
      type: "keymap",
      activeKey: "0x01",
      selectedKey: "0x01,0x00,0xf1,0x00",
      mouseOrKeybord: "mouse",
      hotkeyOrMacro: "hotkey",
      selectedMacroIndex: -1,
      playModel: "0x00",
      repeat: 1,
      selectVisible: false,
      selectKeymap: true,
      selectMacro: true,
      selectDpi: true,
      selectParam: true,
      profileOptions: [
        { value: 0, label: "配置1" },
        { value: 1, label: "配置2" },
        { value: 2, label: "配置3" },
        { value: 3, label: "配置4" },
        { value: 4, label: "配置5" },
        { value: 5, label: "配置6" },
        { value: 6, label: "配置7" },
        { value: 7, label: "配置8" },
      ],
      pendingProfileValue: null,
      lastDPIButtonQueryTime: 0, // 记录上次查询 DPIButton 的时间戳
      importCancelFallbackTimer: null,
    };
  },
  computed: {
    ...mapState("assets", {
      images: "images",
    }),
    ...mapState("loader", {
      inLoader: "inLoader",
      systemInfo: "systemInfo",
      currentDevice: "currentDevice",
      isWeb: "isWeb",
      isQT: "isQT",
    }),
    ...mapState("mouse", {
      profile: "profile",
      keyMatrix: "keyMatrix",
      macroData: "macroData",
      DPI: "DPI",
      param: "param",
      light: "light",
    }),
    selectedMouseProfile() {
      return Number(this.param?.profile ?? 0);
    },
    am30ProfileKeySwitchEnabled: {
      get() {
        return Boolean(Number(this.param?.AM30ProfileKeySwitch ?? 0));
      },
      set(value) {
        this.updateAM30ProfileKeySwitch(value ? 1 : 0);
      },
    },
    availableProfileOptions() {
      const profileMax = Math.max(
        1,
        Math.min(8, Number(this.param?.profileMax ?? 2) || 2)
      );
      return this.profileOptions.filter(
        (profileOption) => profileOption.value < profileMax
      );
    },
    profileMenuOptions() {
      const profileModeMeta = {
        0: { label: this.$t("mouse.profileModes.daily"), dotClass: "green" },
        1: { label: this.$t("mouse.profileModes.game"), dotClass: "yellow" },
      };
      return this.availableProfileOptions.map((profileOption) => {
        const meta = profileModeMeta[profileOption.value];
        return {
          ...profileOption,
          menuLabel: meta
            ? `${profileOption.label}-${meta.label}`
            : profileOption.label,
          dotClass: meta?.dotClass || "green",
        };
      });
    },
    currentProfileLabel() {
      const matchedProfile = this.availableProfileOptions.find(
        (profileOption) => profileOption.value === this.selectedMouseProfile
      );
      return matchedProfile ? matchedProfile.label : "配置1";
    },
    isProfileSwitching() {
      return this.pendingProfileValue !== null;
    },
  },
  methods: {
    // ==================== 步骤加载器场景配置 ====================
    /**
     * 定义步骤加载器的场景配置
     * AM30 没有 resetMouse 成功回调，使用模拟模式 + initMouseData 回调
     */
    stepLoaderScenes() {
      return {
        reset: {
          steps: [
            this.$t("mouse.resetSteps.resetting"),
            this.$t("mouse.resetSteps.resetSuccess"),
            this.$t("mouse.resetSteps.initializing"),
            this.$t("mouse.resetSteps.complete"),
          ],
          timeout: 30000,
          onStart: () => {
            // 订阅初始化完成事件
            deviceCallbackManager.subscribe(
              "initMouseData",
              this._onResetInitComplete
            );
            // 发送重置指令
            this.$store.dispatch("loader/mouseReset");
            // 手动重置前端 store 数据（AM30 重置后设备会上报新数据，但也需要前端先设置默认值）
            this._resetLocalMouseData();
          },
          onComplete: () => {
            this.type = "keymap";
          },
          onTimeout: () => {
            deviceCallbackManager.unsubscribe(
              "initMouseData",
              this._onResetInitComplete
            );
            this.$message.error(this.$t("mouse.resetSteps.fail"));
          },
          // AM30 没有 resetMouse 回调，用模拟推进前两步
          simulate: {
            enabled: true,
            delays: [2000, 1500], // 2s后"重置成功"，再1.5s后"初始化中"
          },
        },
        // 导出场景（仅网页端使用）
        export: {
          steps: [
            this.$t("mouse.exportSteps.exporting"),
            this.$t("mouse.exportSteps.selectPath"),
            this.$t("mouse.exportSteps.complete"),
          ],
          timeout: 60000,
          onStart: (options) => {
            this.pauseMouseBatteryGuard("export");
            // 订阅导出完成回调
            deviceCallbackManager.subscribe(
              "saveMousejson",
              this._onExportComplete
            );
            // 发送导出指令
            this.$store.dispatch("loader/saveMousejson", options.saveData);
            // 下发指令后跳到"选择导出路径"步骤
            setTimeout(() => {
              if (this.slLoading && this.slActiveScene === "export") {
                this.setStepLoaderStep(1);
              }
            }, 200);
          },
          onComplete: () => {
            this.resumeMouseBatteryGuard("export", { refresh: true });
          },
          onTimeout: () => {
            deviceCallbackManager.unsubscribe(
              "saveMousejson",
              this._onExportComplete
            );
            this.resumeMouseBatteryGuard("export", { refresh: true });
            this.$message.error(this.$t("mouse.exportSteps.fail"));
          },
          simulate: {
            enabled: false,
          },
        },
        import: {
          steps: [
            this.$t("mouse.importSteps.selectFile"),
            this.$t("mouse.importSteps.importing"),
            this.$t("mouse.importSteps.success"),
          ],
          timeout: 35000,
          onStart: () => {
            this.pauseMouseBatteryGuard("import");
            this.clearSceneFallbackTimer("importCancelFallbackTimer");
            setTimeout(() => {
              this.$store.dispatch("loader/loadMousejson");
              if (this.slLoading && this.slActiveScene === "import") {
                this.setStepLoaderStep(1);
              }
            }, 100);
          },
          onComplete: () => {
            this.clearSceneFallbackTimer("importCancelFallbackTimer");
            this.type = "keymap";
            this.resumeMouseBatteryGuard("import", { refresh: true });
          },
          onTimeout: () => {
            this.clearSceneFallbackTimer("importCancelFallbackTimer");
            this.resumeMouseBatteryGuard("import", { refresh: true });
            this.$message.error(this.$t("mouse.importSteps.fail"));
          },
          simulate: {
            enabled: false,
          },
        },
        switchProfile: {
          steps: ["切换配置中", "正在初始化", "切换配置成功"],
          timeout: 30000,
          onStart: (options) => {
            const profileValue = Number(options?.profileValue);
            if (Number.isNaN(profileValue)) {
              this.closeStepLoader();
              return;
            }

            this.pauseMouseBatteryGuard("switchProfile");
            this.pendingProfileValue = profileValue;
            deviceCallbackManager.subscribe(
              "initMouseData",
              this._onSwitchProfileInitComplete
            );
            this.$store.dispatch("loader/setMouseProfile", {
              profile: profileValue,
              profileMax: Math.max(
                1,
                Math.min(8, Number(this.param?.profileMax ?? 2) || 2)
              ),
            });
            setTimeout(() => {
              if (this.slLoading && this.slActiveScene === "switchProfile") {
                this.setStepLoaderStep(1);
              }
            }, 1200);
          },
          onComplete: () => {
            this.pendingProfileValue = null;
            this.type = "keymap";
          },
          onTimeout: () => {
            deviceCallbackManager.unsubscribe(
              "initMouseData",
              this._onSwitchProfileInitComplete
            );
            this.pendingProfileValue = null;
            this.resumeMouseBatteryGuard("switchProfile", { refresh: true });
            this.$message.error("切换配置失败");
          },
          simulate: {
            enabled: false,
          },
        },
      };
    },

    pauseMouseBatteryGuard(source) {
      const electricity = this.$refs?.mouseElectricity;
      if (electricity && typeof electricity.pauseBatteryGuard === "function") {
        electricity.pauseBatteryGuard(source);
      }
    },
    resumeMouseBatteryGuard(source, options = {}) {
      const electricity = this.$refs?.mouseElectricity;
      if (electricity && typeof electricity.resumeBatteryGuard === "function") {
        electricity.resumeBatteryGuard(source, options);
      }
    },
    isImportStepLoaderActive() {
      return this.slLoading && this.slActiveScene === "import";
    },
    isExportStepLoaderActive() {
      return this.slLoading && this.slActiveScene === "export";
    },

    /**
     * 重置本地鼠标数据到默认值
     */
    _resetLocalMouseData() {
      // 重置按键映射
      this.$store.dispatch("mouse/setKeyMatrix", {
        "0x00": ["0x01", "0x00", "0xf0", "0x00"],
        "0x02": ["0x01", "0x00", "0xf2", "0x00"],
        "0x01": ["0x01", "0x00", "0xf1", "0x00"],
        "0x0e": ["0x01", "0x00", "0xf5", "0x01"],
        "0x0f": ["0x01", "0x00", "0xf5", "0xff"],
        "0x03": ["0x01", "0x00", "0xf4", "0x00"],
        "0x04": ["0x01", "0x00", "0xf3", "0x00"],
      });

      // 重置 DPI 设置
      const maxDPI = this.param.mouseID === 1935 ? 26000 : 30000;
      this.$store.dispatch("mouse/setDPI", {
        profile: 0,
        currentID: 0,
        isOpen: 1,
        DPItime: [800, 1600, 2400, 3200, 5000, 10000, 20000, maxDPI],
        xDPI: [800, 1600, 2400, 3200, 5000, 10000, 20000, maxDPI],
        yDPI: [800, 1600, 2400, 3200, 5000, 10000, 20000, maxDPI],
        DPIcolor: [
          "#FF0000",
          "#00FF00",
          "#0000FF",
          "#00FFFF",
          "#800080",
          "#FFFF00",
          "#FF00FF",
          "#ffffff",
        ],
        DPIStages: 8,
      });

      // 重置参数设置
      this.$store.dispatch("mouse/setParam", {
        profile: 0,
        profileMax: 2,
        keySensitivity: 0,
        USBDebounce: 3,
        twoPointFourGDebounce: 1,
        BTDebounce: 1,
        buttonChangeTime: 12,
        wheelToButton: 1,
        buttonToWheel: 1,
        sleepOneTime24G: 30,
        sleepOneTimeBT: 600,
        reportRate: 1000,
        liftCutOff: 1,
        motionSync: 1,
        angleSnap: 0,
        rippleCorrection: 0,
        charLight: [255, 255, 255],
        charEndLight: [255, 0, 255],
        charLightswitch: 1,
        AM30ProfileKeySwitch: 0,
        mouseID: this.param.mouseID,
      });
    },

    /**
     * 重置完成回调（收到 initMouseData）
     */
    _onResetInitComplete() {
      if (!this.slLoading) return;
      deviceCallbackManager.unsubscribe(
        "initMouseData",
        this._onResetInitComplete
      );
      this.completeStepLoader("reset");
    },
    _onSwitchProfileInitComplete() {
      if (!this.slLoading || this.slActiveScene !== "switchProfile") return;

      deviceCallbackManager.unsubscribe(
        "initMouseData",
        this._onSwitchProfileInitComplete
      );
      this.resumeMouseBatteryGuard("switchProfile", { refresh: true });
      this.completeStepLoader("switchProfile");
    },

    /**
     * 导出完成回调（收到 saveMousejson）
     */
    _onExportComplete(json) {
      console.log("[Export] saveMousejson callback:", json);
      if (!this.slLoading || this.slActiveScene !== "export") return;

      try {
        let result = json;
        if (typeof json === "string") {
          try {
            const parsed = JSON.parse(json);
            result = parsed.data || parsed;
          } catch {
            result = json;
          }
        } else if (json && typeof json === "object") {
          result = json.data || json;
        }

        if (result === "success") {
          deviceCallbackManager.unsubscribe(
            "saveMousejson",
            this._onExportComplete
          );
          this.completeStepLoader("export");
        }
      } catch (e) {
        console.error("[Export] Parse error:", e);
      }
    },

    keyDesc(k) {
      let desc = "";
      const key = this.keyMatrix[k];
      const allKeys = VirtualKeyboard.allKeys;
      const specialDesc = this.getSpecialKeyDesc(key);
      if (specialDesc) {
        return this.$t("mouse.macroDesc." + specialDesc);
      }
      if (key[0] === "0x0b") {
        desc =
          this.$t("mouse.clickMacro") +
          " [" +
          allKeys[key[1]].text.replace("<br/>", "") +
          "]";
      } else if (key[0] === "0x00") {
        if (key[1] !== "0x00")
          desc += allKeys[key[1]].text.replace("<br/>", "") + " + ";
        if (key[2] !== "0x00")
          desc += allKeys[key[2]].text.replace("<br/>", "") + " + ";
        if (key[3] !== "0x00")
          desc += allKeys[key[3]].text.replace("<br/>", "");
      } else if (key[0] === "0x09") {
        const macroIndex = parseInt(key[2], 16);
        const macro = this.macroData.find((i) => i.index === macroIndex);
        desc =
          this.$t("mouse.macro") +
          (macro ? " " + macro.macro_name : " M_" + macroIndex);
      } else {
        const targetKey = this.allKeys.find(
          (i) => i.key.toString() === key.toString()
        );
        desc = targetKey ? this.$t("mouse.macroDesc." + targetKey.desc) : "";
      }
      return desc;
    },
    getSpecialKeyDesc(keyMapping) {
      if (!Array.isArray(keyMapping) || keyMapping.length === 0) return "";
      return SPECIAL_KEY_DESC_MAP[keyMapping.toString()] || "";
    },
    isMacroKeyMapping(keyMapping) {
      return Array.isArray(keyMapping) && keyMapping[0] === "0x09";
    },
    getMacroIndexFromKeyMapping(keyMapping) {
      if (!this.isMacroKeyMapping(keyMapping)) return -1;
      return parseInt(keyMapping[2], 16);
    },
    saveMacroBindingByProtocol(macroIndex, keyCode, changeKey) {
      const macroData = Array.isArray(this.macroData) ? [...this.macroData] : [];
      const targetIndex = macroData.findIndex((macro) => macro.index === macroIndex);
      if (targetIndex < 0) return;

      const macro = {
        ...macroData[targetIndex],
        key_code: keyCode || "",
        change_key: changeKey || "",
      };
      macroData[targetIndex] = macro;
      this.$store.dispatch("mouse/setMacroData", macroData);
      this.$store.dispatch("loader/saveMouseMacro", macro);
    },
    clearPreviousMacroBindingByProtocol(previousKeyMapping) {
      const previousMacroIndex = this.getMacroIndexFromKeyMapping(previousKeyMapping);
      if (previousMacroIndex < 0) return;

      this.saveMacroBindingByProtocol(previousMacroIndex, "", "");
    },
    keyDisabled(key) {
      const keyMapping = this.keyMatrix?.[key];
      if (!keyMapping || keyMapping.length === 0) return false;

      if (
        keyMapping.toString() === ["0x01", "0x00", "0xf0", "0x00"].toString() &&
        Object.values(this.keyMatrix).filter(
          (value) =>
            value?.toString() === ["0x01", "0x00", "0xf0", "0x00"].toString()
        ).length <= 1
      ) {
        return true;
      }
      return false;
    },
    onMouseClick(key) {
      if (this.keyDisabled(key)) return;

      const keyMapping = this.keyMatrix?.[key];
      if (!keyMapping || keyMapping.length === 0) return;

      this.activeKey = key;
      if (keyMapping[0] === "0x00") {
        this.selectedKey = "0x00,0x00,0x00,0x00";
        this.selectedMacroIndex = -1;
      } else if (keyMapping[0] === "0x09") {
        this.selectedKey = "0x00,0x00,0x00,0x00";
        this.selectedMacroIndex = parseInt(keyMapping[2], 16);
        this.playModel = keyMapping[1];
        const macro = this.macroData.find(
          (i) => i.index === this.selectedMacroIndex
        );
        this.repeat = macro?.repeat;
      } else {
        this.selectedKey = keyMapping.toString();
        this.selectedMacroIndex = -1;
      }
    },
    onSelectedKey(e) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      this.clearPreviousMacroBindingByProtocol(keyMatrix[this.activeKey]);
      this.selectedKey = e.target.value;
      this.selectedMacroIndex = -1;
      keyMatrix[this.activeKey] = this.selectedKey.split(",");
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedKeyboard(value) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      this.clearPreviousMacroBindingByProtocol(keyMatrix[this.activeKey]);
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = -1;
      keyMatrix[this.activeKey] = ["0x00", "0x00", "0x00", "0x00"];
      keyMatrix[this.activeKey][3] = value || "0x00";
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedFirepower(index, value) {
      const keyMatrix = { ...this.keyMatrix };
      this.clearPreviousMacroBindingByProtocol(keyMatrix[this.activeKey]);
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = -1;
      if (keyMatrix[this.activeKey][0] !== "0x0b") {
        keyMatrix[this.activeKey] = ["0x0b", "0x04", "0x01", "0x01"];
      }
      if (!value.includes("0x"))
        value = `0x${Number(value).toString(16).padStart(2, "0")}`;
      keyMatrix[this.activeKey][index] = value;
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedHotkey(index, value) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      this.clearPreviousMacroBindingByProtocol(keyMatrix[this.activeKey]);
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = -1;
      if (keyMatrix[this.activeKey][0] !== "0x00") {
        keyMatrix[this.activeKey] = ["0x00", "0xe0", "0x00", "0x04"];
      }
      keyMatrix[this.activeKey][index] = value || "0x00";
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedMacro(e) {
      if (!this.activeKey) return;
      const keyMatrix = { ...this.keyMatrix };
      const previousMacroIndex = this.getMacroIndexFromKeyMapping(
        keyMatrix[this.activeKey]
      );
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.selectedMacroIndex = Number(e.target.value);
      if (
        previousMacroIndex > -1 &&
        previousMacroIndex !== this.selectedMacroIndex
      ) {
        this.saveMacroBindingByProtocol(previousMacroIndex, "", "");
      }
      keyMatrix[this.activeKey] = [
        "0x09",
        this.playModel,
        `0x${this.selectedMacroIndex.toString(16).padStart(2, "0")}`,
        "0x00",
      ];
      const macro = this.macroData.find(
        (i) => i.index === this.selectedMacroIndex
      );
      this.repeat = macro.repeat;
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onSelectedPlayModel(e) {
      const keyMatrix = { ...this.keyMatrix };
      this.repeat = 1;
      this.playModel = e.target.value;
      keyMatrix[this.activeKey][1] = this.playModel;
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    onRepeatChange(e) {
      this.repeat = e.target.value;
      this.onSure();
    },
    onDisable() {
      const keyMatrix = { ...this.keyMatrix };
      this.clearPreviousMacroBindingByProtocol(keyMatrix[this.activeKey]);
      keyMatrix[this.activeKey] = ["0x00", "0x00", "0x00", "0x00"];
      this.selectedKey = "0x00,0x00,0x00,0x00";
      this.$store.dispatch("mouse/setKeyMatrix", { ...keyMatrix });
      this.onSure();
    },
    clearMacroSelectionState() {
      this.selectedMacroIndex = -1;
      this.playModel = "0x00";
      this.repeat = 1;
    },
    onRestore() {
      const keyMatrix = {
        "0x00": ["0x01", "0x00", "0xf0", "0x00"],
        "0x02": ["0x01", "0x00", "0xf2", "0x00"],
        "0x01": ["0x01", "0x00", "0xf1", "0x00"],
        "0x0e": ["0x01", "0x00", "0xf5", "0x01"],
        "0x0f": ["0x01", "0x00", "0xf5", "0xff"],
        "0x03": ["0x01", "0x00", "0xf4", "0x00"],
        "0x04": ["0x01", "0x00", "0xf3", "0x00"],
      };
      const defaultMapping = keyMatrix[this.activeKey];
      this.clearPreviousMacroBindingByProtocol(this.keyMatrix[this.activeKey]);
      this.selectedKey = defaultMapping.toString();
      this.clearMacroSelectionState();
      this.$store.dispatch("mouse/setKeyMatrix", {
        ...this.keyMatrix,
        [this.activeKey]: defaultMapping,
      });
      this.$store.dispatch("loader/changeMouseKey", {
        key_code: this.activeKey,
        change_key: defaultMapping,
      });
    },
    onSure() {
      if (this.keyMatrix[this.activeKey][0] === "0x0b") {
        const change_key = [...this.keyMatrix[this.activeKey]];
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key,
        });
      } else if (this.keyMatrix[this.activeKey][0] === "0x00") {
        const change_key = [...this.keyMatrix[this.activeKey]];
        if (change_key[3] != "0x00" && change_key[2] == "0x00") {
          change_key[2] = change_key[3];
          change_key[3] = "0x00";
        }
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key,
        });
      } else if (this.selectedMacroIndex > -1) {
        let macro = this.macroData.find(
          (i) => i.index === this.selectedMacroIndex
        );
        macro.repeat = parseInt(this.repeat);
        macro.key_code = this.activeKey;
        macro.playModel = this.playModel;
        macro = { ...macro };
        macro.change_key = [
          "0x09",
          this.playModel,
          `0x${macro.index.toString(16).padStart(2, "0")}`,
          "0x00",
        ];
        this.saveMacroBindingByProtocol(macro.index, this.activeKey, macro.change_key);
        this.$store.dispatch("loader/setMouseMacro", macro);
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key: macro.change_key,
        });
        // this.$store.dispatch("mouse/setMacroData", [...this.macroData]);
      } else {
        this.$store.dispatch("loader/changeMouseKey", {
          key_code: this.activeKey,
          change_key: this.selectedKey.split(","),
        });
      }
      // this.$message.info(this.$t("loader.saveSuccess"));
    },
    onReset() {
      this.$confirm({
        title: this.$t("mouse.reset"),
        okText: this.$t("yes"),
        cancelText: this.$t("cancel"),
        centered: true,
        onOk: () => {
          this.startStepLoader("reset");
        },
      });
    },
    onSwitchProfile(profileValue) {
      const nextProfile = Number(profileValue);
      if (
        this.isProfileSwitching ||
        Number.isNaN(nextProfile) ||
        nextProfile === this.selectedMouseProfile
      ) {
        return;
      }
      this.startStepLoader("switchProfile", { profileValue: nextProfile });
    },
    requestAM30ProfileKeySwitch() {
      this.$store.dispatch("loader/getAM30ProfileKeySwitch");
    },
    updateAM30ProfileKeySwitch(nextValue) {
      const payload = {
        AM30ProfileKeySwitch: Number(nextValue ? 1 : 0),
      };
      this.$store.dispatch("mouse/setParam", payload);
      this.$store.dispatch("loader/setAM30ProfileKeySwitch", payload);
    },
    onUpload() {
      setTimeout(() => {
        if (this.type === "upload") {
          this.type = "keymap";
        }
      }, 10);

      if (this.isWeb || this.isQT) {
        this.startStepLoader("import");
        return;
      }

      this.pauseMouseBatteryGuard("import");
      this.$store.dispatch("loader/setLoading", { loading: true, time: 30000 });
      setTimeout(() => {
        this.$store.dispatch("loader/loadMousejson");
      }, 100);
    },
    onDownload() {
      setTimeout(() => (this.type = ""), 10);
      this.selectVisible = false;
      const KeyMatrix = { ...this.keyMatrix };
      for (const key in KeyMatrix) {
        let item = KeyMatrix[key];
        if (item[0] == "0x00" && item[3] != "0x00" && item[2] == "0x00") {
          item[2] = item[3];
          item[3] = "0x00";
        }
      }

      // 构建导出数据
      const saveData = {
        profile: this.profile,
        KeyMatrix: this.selectKeymap ? KeyMatrix : null,
        mouseMacroData: this.selectMacro
          ? this.macroData.map((i) => ({ ...i }))
          : null,
        mouseDPI: this.selectDpi ? this.DPI : null,
        mouseArgs: this.selectParam ? this.param : null,
        mouseLight: this.selectParam ? this.light : null,
      };

      // Qt 模式：使用原来的 loading
      // 网页模式：使用 StepLoader
      if (this.isWeb) {
        this.startStepLoader("export", { saveData });
      } else {
        this.pauseMouseBatteryGuard("export");
        this.$store.dispatch("loader/setLoading", true);
        this.$store.dispatch("loader/saveMousejson", saveData);
      }
    },
    onHostInitMouseData(json) {
      if (json) {
        this.clearSceneFallbackTimer("importCancelFallbackTimer");
        const isImportStepLoaderActive = this.isImportStepLoaderActive();
        console.log("onHostInitMouseData", json);
        const data = JSON.parse(json);
        data.mouseDPI.isOpen = 1;
        data.mouseDPI.xDPI.map((x, index) => {
          if (x != data.mouseDPI.yDPI[index]) {
            data.mouseDPI.isOpen = 0;
          }
        });
        this.$store.dispatch("mouse/setKeyMatrix", data.KeyMatrix);
        this.$store.dispatch("mouse/setMacroData", data.mouseMacroData);
        this.$store.dispatch("mouse/setDPI", data.mouseDPI);
        this.$store.dispatch("mouse/setParam", data.mouseArgs);
        this.requestAM30ProfileKeySwitch();
        this.$store.dispatch("loader/setLoading", false);
        this.$nextTick(() => {
          this.onMouseClick(this.activeKey);
          if (isImportStepLoaderActive) {
            this.completeStepLoader("import");
            return;
          }
          this.resumeMouseBatteryGuard("import", { refresh: true });
        });
        console.log("onHostInitMouseData", "true");
      }
    },
    onHostUpdateMouseSet(json) {
      console.log("onHostUpdateMouseSet", json);
      const data = JSON.parse(json);
      if (data) {
        this.$store.dispatch("mouse/setKeyMatrix", data.KeyMatrix);
        console.log("updateMouseSet", "true");
      }
    },
    onHostInitMouseMacros(json) {
      console.log("onHostInitMouseMacros", json);
      const data = JSON.parse(json);
      if (data) {
        this.$store.dispatch("mouse/setMacroData", data.mouseMacroData);
        console.log("initMouseMacros", "true");
      }
    },
    onHostUpdateMouseDPI(json) {
      console.log("onHostUpdateMouseDPI", json);
      const data = JSON.parse(json);
      if (data) {
        data.isOpen = 1;
        data.xDPI.map((x, index) => {
          if (x != data.yDPI[index]) {
            data.isOpen = 0;
          }
        });
        this.$store.dispatch("mouse/setDPI", data);
        console.log("updateMouseDPI", "true");
      }
    },
    onHostUpdateMouseInfo(json) {
      console.log("onHostUpdateMouseInfo", json);
      const data = JSON.parse(json);
      if (data) {
        this.$store.dispatch("mouse/setParam", data);
        console.log("updateMouseInfo", "true");
      }
    },
    onHostGetAM30ProfileKeySwitch(json) {
      console.log("onHostGetAM30ProfileKeySwitch", json);
      const data = JSON.parse(json);
      if (data && data.AM30ProfileKeySwitch != null) {
        this.$store.dispatch("mouse/setParam", {
          AM30ProfileKeySwitch: Number(data.AM30ProfileKeySwitch),
        });
      }
    },
    onHostUpdateFinish() {
      console.log("updateFinish");
    },
    onHostLoadingFinish(payload) {
      if (
        this.handleSceneLoadingFinish({
          payload,
          sceneName: "import",
          timerKey: "importCancelFallbackTimer",
          isSceneActive: () => this.isImportStepLoaderActive(),
          cancelLog: "cancel import due to loadingFinish",
          ignoreLog: "ignore loadingFinish during import",
          onCancel: () => {
            this.closeStepLoader();
            this.resumeMouseBatteryGuard("import", { refresh: true });
            this.$store.dispatch("loader/setLoading", false);
          },
        })
      ) {
        return;
      }
      if (this.isExportStepLoaderActive()) {
        console.log("ignore loadingFinish during step loader scene");
        return;
      }
      this.resumeMouseBatteryGuard("import", { refresh: true });
      this.resumeMouseBatteryGuard("export", { refresh: true });
      this.$store.dispatch("loader/setLoading", false);
      console.log("loadingFinish");
    },
    onHostUpdateDPIButton(json) {
      console.log("Bridge", "updateDPIButton", json);
      try {
        const data = JSON.parse(json);
        if (data && data.DPIButtonStatus !== undefined) {
          const setDPIButton = Number(data.DPIButtonStatus);
          this.$store.dispatch("mouse/setDPI", {
            ...this.DPI,
            setDPIButton: setDPIButton
          });
          console.log("Bridge", "updateDPIButton", 'success',setDPIButton);
        } else {
          console.log("Bridge", "updateDPIButton", "Invalid data or missing DPIButtonStatus");
        }
      } catch (error) {
        console.info("Bridge", "updateDPIButton", "Error:", error);
      }
    },
    onHostUpdateFPS(json) {
      console.log("Bridge", "updateFPS", json);
      try {
        if (json !== '') {
          const fpsMode = Number(json);
          this.$store.dispatch("mouse/setParam", {
            ...this.param,
            fpsMode: fpsMode
          });
        } else {
          console.log("Bridge", "updateFPS", "Invalid data or missing FPSStatus");
        }
      } catch (error) {
        console.info("Bridge", "updateFPS", "Error:", error);
      }
    },
    // ==================== Host 事件处理 ====================
    subscribeToHostEvents() {
      deviceCallbackManager.subscribe('initMouseData', this.onHostInitMouseData);
      deviceCallbackManager.subscribe('updateMouseSet', this.onHostUpdateMouseSet);
      deviceCallbackManager.subscribe('initMouseMacros', this.onHostInitMouseMacros);
      deviceCallbackManager.subscribe('updateMouseDPI', this.onHostUpdateMouseDPI);
      deviceCallbackManager.subscribe('updateMouseInfo', this.onHostUpdateMouseInfo);
      deviceCallbackManager.subscribe('getAM30ProfileKeySwitch', this.onHostGetAM30ProfileKeySwitch);
      deviceCallbackManager.subscribe('updateFinish', this.onHostUpdateFinish);
      deviceCallbackManager.subscribe('loadingFinish', this.onHostLoadingFinish);
      deviceCallbackManager.subscribe('updateDPIButton', this.onHostUpdateDPIButton);
      deviceCallbackManager.subscribe('updateFPS', this.onHostUpdateFPS);
    },
    unsubscribeFromHostEvents() {
      deviceCallbackManager.unsubscribe('initMouseData', this.onHostInitMouseData);
      deviceCallbackManager.unsubscribe('updateMouseSet', this.onHostUpdateMouseSet);
      deviceCallbackManager.unsubscribe('initMouseMacros', this.onHostInitMouseMacros);
      deviceCallbackManager.unsubscribe('updateMouseDPI', this.onHostUpdateMouseDPI);
      deviceCallbackManager.unsubscribe('updateMouseInfo', this.onHostUpdateMouseInfo);
      deviceCallbackManager.unsubscribe('getAM30ProfileKeySwitch', this.onHostGetAM30ProfileKeySwitch);
      deviceCallbackManager.unsubscribe('updateFinish', this.onHostUpdateFinish);
      deviceCallbackManager.unsubscribe('loadingFinish', this.onHostLoadingFinish);
      deviceCallbackManager.unsubscribe('updateDPIButton', this.onHostUpdateDPIButton);
      deviceCallbackManager.unsubscribe('updateFPS', this.onHostUpdateFPS);
      deviceCallbackManager.unsubscribe('initMouseData', this._onSwitchProfileInitComplete);
    },
  },
  watch: {
    type(type) {
      if (type === "keymap") this.onMouseClick(this.activeKey);
    },
  },
  mounted() {
    this.$store.dispatch("loader/setInLoader", true);
    this.$store.dispatch("loader/initMouseData");
    this.$store.dispatch("loader/setLoading", true);

    this.subscribeToHostEvents();

    if (window.location.href.includes("localhost")) {
      setTimeout(() => {
        // 组件已销毁则不执行
        if (this._isDestroyed) return;
        window.initMouseData(
          JSON.stringify({
            mouseMacroData: [
              {
                index: 0,
                profile: 0,
                key_code: "0x04",
                repeat: 3,
                playModel: "0x00",
                macroValue: {
                  keyOrder: ["0x111e", "0x101e"],
                  pressTime: [64, 74],
                },
                change_key: ["0x09", "0x00", "0x00", "0x00"],
              },
            ],
            mouseDPI: {
              isOpen: 0,
              profile: 0,
              currentID: 4,
              xDPI: [400, 800, 1200, 1600, 3000, 6400, 10000, 30000],
              yDPI: [400, 800, 1200, 1600, 3000, 6400, 10000, 30000],
              DPIcolor: [
                "#FF0000",
                "#FF3200",
                "#FFFF00",
                "#00FF00",
                "#00FFFF",
                "#0000FF",
                "#FF00FF",
                "#FFFFFF",
              ],
              DPImax:5,
              setDPIButton: 1
            },
            mouseArgs: {
              profile: 0,
              profileMax: 2,
              reportRate: 8000,
              
              rippleCorrection: 0,
              buttonChangeTime: 12,
              wheelToButton: 1,
              buttonToWheel: 1,
              sleepOneTimeBT: 600,
              sleepOneTime24G: 30,
              liftCutOff: 1,
              angleSnap: 0,
              setSNEDORAngle: 15,
              mouseID: 2222,
              charLight: [255, 255, 255],
              charEndLight: [255, 255, 255],
              charLightswitch: 0,
              USBDebounce: 15, // USB 去抖动
              twoPointFourGDebounce: 7, // 2.4G 
              BTDebounce: 3, // 蓝牙去抖动 
              motionSync: 1,
            },
            KeyMatrix: {
              "0x00": ["0x01", "0x00", "0xf0", "0x00"],
              "0x02": ["0x01", "0x00", "0xf2", "0x00"],
              "0x01": ["0x09", "0x00", "0x00", "0x00"],
              "0x0e": ["0x01", "0x00", "0xf5", "0x01"],
              "0x0f": ["0x01", "0x00", "0xf5", "0xff"],
              "0x03": ["0x01", "0x00", "0xf4", "0x00"],
              "0x04": ["0x01", "0x00", "0xf3", "0x00"],
            },
          })
        );
        this.$store.dispatch("loader/setLoading", false);
      }, 1000);
    }
  },
  
  destroyed() {
    this.clearSceneFallbackTimer("importCancelFallbackTimer");
    this.unsubscribeFromHostEvents();
    this.cleanupStepLoaderOnDestroy();
  },
};
</script>
<style lang="scss" scoped>
.mouse {
  font-size: 16px;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 150px;
  background: #171717;

  .fixed-panel.px {
    width: 100%;
    background: #171717;
    padding: 60px 0 20px 0;
    position: fixed;
    z-index: 1;
    top: 0;

    /* 屏幕高度不足时减小padding，但保持固定定位 */
    @media screen and (max-height: 820px) {
      padding: 60px 0 0 0;
    }
  }

  .mouse-panel {
    @include flex-center;

    .keys {
      width: 400px;
      height: 400px;
      background: url("../../assets/images/mouse.png") center / auto 100%
        no-repeat;
      position: relative;

      .key {
        width: 300px;
        height: 35px;
        line-height: 35px;
        position: absolute;
        font-size: 20px;
        border-bottom: 1px solid #bf94ff;
        opacity: 0.5;
        white-space: nowrap;
        cursor: pointer;

        &:hover,
        &.active {
          opacity: 1;
          color: $primary-color;
          font-size: 24px;
        }

        &:hover {
          background-color: #ffffff11;
        }

        &.disabled {
          cursor: not-allowed;
        }

        &::before {
          display: block;
          content: "";
          width: 50px;
          height: 0;
          border-top: 1px solid #bf94ff;
          transform: rotate(45deg);
          position: absolute;
          right: 0;
          bottom: 0;
        }

        &::after {
          display: block;
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 8px;
          background: #bf94ff;
          position: absolute;
          right: 0;
          bottom: 0;
        }
      }

      .key1 {
        top: -5%;
        left: -47%;

        &::before {
          right: -43px;
          bottom: -18px;
        }

        &::after {
          right: -43px;
          bottom: -43px;
        }
      }

      .key2 {
        top: 11%;
        left: 74%;
        text-align: right;

        &::before {
          width: 100px;
          transform: rotate(0);
          left: -100px;
          bottom: -2px;
        }

        &::after {
          left: -100px;
          bottom: -4px;
        }
      }

      .key3 {
        top: -5%;
        left: 74%;
        text-align: right;

        &::before {
          transform: rotate(135deg);
          left: -43px;
          bottom: -18px;
        }

        &::after {
          left: -43px;
          bottom: -43px;
        }
      }

      .key4 {
        top: 14%;
        left: -34.5%;

        &::before {
          transform: rotate(135deg);
          right: -43px;
          bottom: 17px;
        }

        &::after {
          right: -43px;
          bottom: 33px;
        }
      }

      .key5 {
        top: 27%;
        left: 60%;
        text-align: right;

        &::before {
          left: -43px;
          bottom: 17px;
        }

        &::after {
          left: -43px;
          bottom: 34px;
        }
      }

      .key6 {
        top: 31%;
        left: -62%;

        &::before {
          transform: rotate(0);
          right: -50px;
          bottom: -1px;
        }

        &::after {
          right: -50px;
          bottom: -4px;
        }
      }

      .key7 {
        top: 48%;
        left: -62%;

        &::before {
          transform: rotate(0);
          right: -50px;
          bottom: -1px;
        }

        &::after {
          right: -50px;
          bottom: -4px;
        }
      }
    }
  }

  .radio-panel {
    @include flex-center;
    width: 1200px;
    margin: 0 auto 0 auto;
    justify-content: flex-end;
    align-items: center;
    white-space: nowrap;
    gap: 6px;

    .tab-group {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0;
      border: 1px solid $primary-color;
      border-radius: 10px;
      overflow: hidden;
      background: transparent;
    }

    .virtual-radio {
      display: flex;
      justify-content: flex-start;

      #file {
        display: none;
      }
    }

    .function-tabs {
      flex: 0 0 auto;
    }

    .profile-action-tabs {
      flex: 0 0 auto;
      gap: 0;
    }

    .action-radio {
      flex: 0 0 auto;
    }

    .profile-dropdown {
      flex: 0 0 auto;
    }

    .profile-trigger {
      @include flex-center;
      min-width: 94px;
      height: 38px;
      padding: 0 12px;
      color: $primary-color;
      cursor: pointer;
      user-select: none;
      background: transparent;

      &.disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    }

    .tab-trigger {
      border-right: 1px solid $primary-color;
    }

    .profile-trigger-label {
      font-size: 16px;
      line-height: 1;
    }

    .profile-trigger-arrow {
      font-size: 10px;
      margin-left: 6px;
      transform: none;
    }

    .ant-radio-group {
      .ant-radio-button-wrapper {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 38px;
        padding: 0 14px;
        line-height: 38px;
        border-top: 0;
        border-bottom: 0;

        &:first-child {
          border-left: 0;
          border-radius: 0;
        }

        &:last-child {
          border-right: 0;
          border-radius: 0;
        }

        &.ant-radio-button-wrapper-checked {
          box-shadow: none;
        }
      }
    }
  }

  .settings-panel {
    @include flex-center;
    margin-top: calc(500px - 4vw);
    margin-bottom: 60px;
    position: relative;
    z-index: 0;
  }

  .panels {
    @include flex-center;
    flex-wrap: wrap;
    width: 1500px;

    .buttons {
      @include flex-center;
      width: 400px;
      height: 100px;

      .button {
        @include button;
        width: 180px;
        height: 34px;
        margin: 10px;
        border: 1px solid $primary-color;
        border-radius: 5px;
        background: transparent;
        color: $primary-color;

        &:hover {
          color: #ffffff;
        }
      }
    }
  }

  .panel {
    width: 370px;
    height: 340px;
    background-image: linear-gradient(270deg, #ee92ff 0%, #42bfe9 100%);
    box-shadow: 0 0 20px 0 #4136f1;
    border-radius: 15px;
    padding: 3px;
    margin: 0 20px;
    /* 兼容性修复：开启GPU加速，优化渲染层级 */
    transform: translateZ(0);
    will-change: transform;

    .panel-content {
      width: 100%;
      height: 100%;
      padding: 5px 15px;
      background-color: #000000;
      border-radius: 15px;
      /* 兼容性修复：防止背景色溢出覆盖父元素padding区域 */
      background-clip: padding-box;
      display: flex;
      flex-direction: column;

      .title {
        @include flex-center;
        width: 100%;
        font-size: 24px;
        line-height: 50px;
        height: 50px;
        color: $primary-color;
        flex: none;

        .half {
          width: 50%;
          height: 42px;
          text-align: center;
          color: #999999;
          cursor: pointer;

          &:hover {
            color: $primary-color;
          }
        }

        .active {
          color: $primary-color;
          border-bottom: 2px solid $primary-color;
        }
      }
    }

    .radio-group {
      width: 100%;
      height: 100%;
      overflow-y: scroll;
      flex: auto;

      &::-webkit-scrollbar {
        width: 10px;
      }

      .radio {
        @include flex-center;
        height: 50px;
        line-height: 50px;
        justify-content: flex-start;
        font-size: 20px;

        &.ant-radio-wrapper-checked,
        &:hover {
          color: #ffffff;
        }
      }
    }

    .play-model {
      @include flex-center;
      height: 60px;
      color: #ffffff;
      flex: none;

      .radio {
        @include flex-center;
        justify-content: flex-start;
        margin: 0 10px;
      }
    }

    &.hotkey {
      .panel-content {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        align-content: flex-start;
        color: $primary-color;
        font-size: 16px;

        .sub-title {
          width: 100%;
          font-size: 20px;
          color: $primary-color;
          flex: none;
          margin-bottom: -10px;

          &.sub-title-with-tip {
            @include flex-center;
            justify-content: flex-start;
            gap: 8px;
          }
        }

        .name-tip {
          color: #787979;
          font-size: 18px;
          cursor: pointer;
        }

        .name-question {
          width: 320px;
          font-size: 16px;
          line-height: 1.6;
          color: #ffffff;
          white-space: normal;
        }
      }

      .radio-group {
        width: 100px;
        height: auto;
        overflow-y: auto;
        flex: none;
      }

      .checkbox-group {
        display: flex;
        flex-direction: column;
      }

      .radio,
      .checkbox {
        @include flex-center;
        height: 40px;
        line-height: 40px;
        justify-content: flex-start;
        background-color: #050505;
        color: #ffffff;
        padding: 0 5px;
        margin: 5px 5px 0 5px;
        border-radius: 5px;
        font-size: 18px;
      }
    }
  }
}
</style>

<style lang="scss">
.mouse {
  .panel {
    .radio-group .radio {
      .ant-radio .ant-radio-inner {
        border-color: $primary-color;

        &::after {
          background-color: $primary-color;
        }
      }
    }

    &.keybord .virtual-key {
      margin: 80px auto 0 auto;
    }

    &.hotkey {
      .radio-group .radio {
        .ant-radio {
          margin-right: 0;
        }
      }
      .md-checkbox {
        .md-checkbox-container {
          border-radius: 2px;
          border-color: $primary-color;
        }

        label {
          padding-left: 5px;
        }
      }
      .md-checkbox.md-theme-default.md-checked .md-checkbox-container {
        background-color: $primary-color;
      }
    }

    .play-model .radio {
      .ant-radio {
        margin-right: 5px;
      }

      span:not(.ant-radio) {
        @include flex-center;
        padding: 0;
        font-size: 14px;
        color: #ffffff;
      }
    }

    .repeat {
      width: 60px;
      margin-left: 5px;
      font-size: 14px;
    }
  }
}

.c-select {
  @include flex-center;
  width: 900px;
  flex-direction: column;
  margin: 0 auto;
  position: relative;

  .title {
    font-size: 24px;
    color: #ffffff;
    margin-bottom: 30px;
  }

  .checkbox {
    width: 150px;
    font-size: 20px;
    color: #ffffff;
    margin: 0 auto 30px auto;
    text-align: left;
    white-space: nowrap;
  }

  .buttons {
    @include flex-center;
    width: 100%;
    margin-top: 10px;

    .button {
      width: 150px;
      height: 53px;
      background-color: $active-color;
      border-radius: 5px;
      font-size: 20px;
      text-transform: none;

      & + .button {
        margin-left: 100px;
      }
    }
  }

  &.cn .checkbox {
    width: 160px;
  }
}

.am30-profile-menu {
  min-width: 180px;
  background: #171717;
  border: 1px solid #bf94ff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.am30-profile-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(191, 148, 255, 0.2);
  color: $primary-color;

  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.am30-profile-switch-label {
  font-size: 16px;
  line-height: 1.4;
}

.am30-profile-menu-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 17px;
  line-height: 1.4;
  color: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover,
  &.active {
    background: rgba(191, 148, 255, 0.16);
    color: $primary-color;
  }

  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.am30-profile-menu-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
  margin-right: 12px;

  &.green {
    background: #57d17b;
  }

  &.yellow {
    background: #f4d35e;
  }
}

.am30-profile-menu-text {
  white-space: nowrap;
}

.am30-profile-dropdown {
  .ant-dropdown {
    transform-origin: top center;
    will-change: transform, opacity;
  }

  .am30-profile-menu {
    min-width: 180px;
  }

  .md-switch.md-theme-default.md-checked.md-primary .md-switch-container {
    background-color: $primary-color;
  }
}
</style>
